import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAdminOrgDetail } from "@/hooks/queries/useAdminOrgDetail";
import { useUpdateOrganization } from "@/hooks/queries/useUpdateOrganization";
import type {
  AdminOrgDetail,
  UpdateOrganizationRequest,
} from "@/hooks/queries/admin-orgs-types";
import { SEAT_STAYS_CONSUMED_SENTENCE } from "@/routes/org-admin/dashboard-derivations";
import { AdjustSeatCapacityControl } from "@/routes/admin/AdjustSeatCapacityControl";
import { ContractCard } from "@/routes/admin/ContractCard";
import { ExtendContractControl } from "@/routes/admin/ExtendContractControl";
import {
  canRenewContract,
  RenewContractControl,
} from "@/routes/admin/RenewContractControl";
import {
  toAdminOrgError,
  type AdminOrgBanner,
  type AdminOrgFieldErrors,
} from "@/routes/admin/admin-orgs-error";
import { EMPTY_CELL } from "@/routes/admin/admin-orgs-derivations";

// Screen 24 — the platform organization detail (Story 15.6, FR73/FR74/FR78/FR83).
//
// It REPLACES `OrgDetailPlaceholder` at the same route, the same guard and the
// same navigation target 15.1 registered (seam-151): the leaf is swapped, not
// duplicated, and no second route is added for `/admin/orgs/:orgId`.
//
// ONE READ feeds the whole page. The layout is a three-stat seat header over a
// two-card grid — NOT the "four cards" the epic text says (corrected in the
// story's §Corrections): the newer client-validated mockup holds a three-`stat`
// header plus a `grid-2` of exactly two cards, and the Seats "card" IS that stat
// header while the org metadata lives in the content-header sub-line.
//
// SAVE AND CANCEL LIVE IN THE CONTENT HEADER, not a page footer. The mockup puts
// them there and UX 13.13 already agrees ("Header CTAs"); the epic was the only
// artifact saying footer. Cancel goes to `/admin/orgs` — the destination 15.1
// dropped its own Cancel for lack of.
//
// FIVE SURFACES ARE ABSENT ON PURPOSE, and the story asserts their absence:
//   • the members table              → replaced by the two `/admin/users?org_id=`
//                                      deep-links below (FR75, epics.md:3307)
//   • the "Invite org admin" CTA     → `administration/invite_user` pins
//                                      `role: Literal["clinician"]`; there is no
//                                      org-admin invitation path on this platform
//                                      and this story does not create one
//   • the "Operations log (recent)"  → Story 15.7's activity timeline
//   • the "Open org console" link    → it points at Screen 25, whose capability
//                                      `view_org_admin_dashboard` DENIES
//                                      super_admin, so the link would 403
//   • "Archive organization" and any ownership-transfer control → FR73 says
//     ownership cannot be transferred in-product, and archive is hidden in MVP

const HEADING_ID = "admin-org-detail-heading";

/** The six editable values, in the client's own camelCase. */
interface OrgDraft {
  name: string;
  externalReferenceId: string;
  contractStartDate: string;
  billingContactEmail: string;
  securityContactEmail: string;
  billingAddress: string;
}

function draftFrom(detail: AdminOrgDetail): OrgDraft {
  return {
    name: detail.name,
    externalReferenceId: detail.external_reference_id ?? "",
    contractStartDate: detail.contract_start_date ?? "",
    billingContactEmail: detail.billing_contact_email ?? "",
    securityContactEmail: detail.security_contact_email ?? "",
    billingAddress: detail.billing_address ?? "",
  };
}

/**
 * The two controls this form cannot submit empty, with the sentence each shows.
 *
 * Stated precisely, because the obvious reason is wrong for one of them: this is
 * NOT "the two NOT NULL columns". `organizations.name` is `nullable=False`, but
 * `organizations.contract_start_date` is `nullable=True` — which is exactly why
 * records with no start date exist and why this screen must stay able to edit
 * them. What refuses a cleared start date is the REQUEST contract, not the
 * column: `UpdateOrganizationRequest.contract_start_date` is annotated
 * non-Optional, so an explicit `null` is a 422, and `_shared/contract_guard.py`
 * fails closed on a NULL contract date once stored — so clearing a start date
 * that a record already has would write-lock the organization.
 *
 * This screen never sends `null` for either. What changed is who gets told: the
 * refusal is the operator's now, on the control they emptied, instead of a drop
 * this filter performed quietly. See `clearedRequiredFields`.
 *
 * The copy deliberately does NOT say "press Cancel to restore it": Cancel here is
 * a `<Link to="/admin/orgs">`, a route change and not a form reset, so following
 * that instruction would discard every OTHER edit in progress. Two sibling
 * dialogs render their own "Cancel" on this page as well.
 */
const CLEARED_FIELD_MESSAGES = {
  name: "Enter a name to continue.",
  contractStartDate: "Enter a start date to continue.",
} as const;

/**
 * Which required controls the operator has emptied against a stored value.
 *
 * Keyed on the BASELINE having had a value: an organization whose start date
 * was never set is not "cleared" by leaving the control empty, and refusing
 * that would lock the console out of exactly the records it exists to repair —
 * the ones the nullable column lets exist in the first place.
 */
function clearedRequiredFields(
  draft: OrgDraft,
  baseline: OrgDraft,
): (keyof typeof CLEARED_FIELD_MESSAGES)[] {
  const cleared: (keyof typeof CLEARED_FIELD_MESSAGES)[] = [];

  if (baseline.name.trim() !== "" && draft.name.trim() === "") {
    cleared.push("name");
  }
  if (baseline.contractStartDate !== "" && draft.contractStartDate === "") {
    cleared.push("contractStartDate");
  }

  return cleared;
}

/**
 * The request body for exactly the fields that MOVED.
 *
 * Omission and an explicit `null` are not interchangeable on this endpoint — an
 * omitted key is left unchanged, a `null` CLEARS a nullable column — so an
 * unchanged field is never sent at all. `name` and `contract_start_date` are
 * never sent as `null` (both would be a 422): a cleared name or start date is
 * simply NOT SUBMITTED.
 *
 * That omission is still the conservative behaviour, but it is no longer the
 * whole story, and this comment is no longer the only place it is recorded.
 * `clearedRequiredFields` detects the same condition and the screen refuses the
 * save on it, so the two-field drop below can never be the thing an operator
 * discovers by watching a value reappear: it is unreachable while a cleared
 * control is on screen, because Save is disabled and the mutation does not fire.
 */
function changedFields(draft: OrgDraft, baseline: OrgDraft): UpdateOrganizationRequest {
  const body: UpdateOrganizationRequest = {};

  if (draft.name.trim() !== baseline.name.trim() && draft.name.trim() !== "") {
    body.name = draft.name.trim();
  }
  if (draft.externalReferenceId.trim() !== baseline.externalReferenceId.trim()) {
    // A blank normalises to null on the server too (AC11) — many NULLs are
    // admissible on the UNIQUE index, exactly one empty string is.
    body.external_reference_id = draft.externalReferenceId.trim() || null;
  }
  if (
    draft.contractStartDate !== baseline.contractStartDate &&
    draft.contractStartDate !== ""
  ) {
    body.contract_start_date = draft.contractStartDate;
  }
  if (draft.billingContactEmail.trim() !== baseline.billingContactEmail.trim()) {
    body.billing_contact_email = draft.billingContactEmail.trim() || null;
  }
  if (draft.securityContactEmail.trim() !== baseline.securityContactEmail.trim()) {
    body.security_contact_email = draft.securityContactEmail.trim() || null;
  }
  if (draft.billingAddress.trim() !== baseline.billingAddress.trim()) {
    body.billing_address = draft.billingAddress.trim() || null;
  }

  return body;
}

/**
 * One of the four seat figures. Tabular-nums so the row aligns as a set.
 *
 * GUARDED HERE RATHER THAN AT THE FOUR CALL SITES. `active_seat_count` is typed
 * optional (`admin-orgs-types.ts`, the recorded exception to its OPTIONALITY
 * RULE) because an API deployed before Story 21.4 omits it, and a labelled tile
 * rendering nothing is worse than one saying it has no figure. This project's
 * `tsconfig` runs with `strictNullChecks: false`, so the compiler will NOT flag
 * the absent value for us — the guard has to be written, not waited for.
 *
 * The em-dash is the convention Screen 23's cells already use (`EMPTY_CELL`),
 * so the two screens answer a missing figure the same way.
 */
function SeatStat({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="flex flex-col gap-1 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] p-[16px]">
      <span className="text-sm text-[color:var(--text-tertiary)]">{label}</span>
      <strong className="display-xs text-[color:var(--text-primary)] [font-variant-numeric:tabular-nums]">
        {typeof value === "number" ? value : EMPTY_CELL}
      </strong>
    </div>
  );
}

function RefusalBanner({ banner }: { banner: AdminOrgBanner }) {
  return (
    <Banner variant={banner.variant} title={banner.title}>
      <p>{banner.body}</p>
      {banner.code ? <p className="text-sm">{`Reference code: ${banner.code}`}</p> : null}
      <Button asChild variant="secondary" size="sm">
        <Link to="/admin/orgs">Back to organizations</Link>
      </Button>
    </Banner>
  );
}

export function OrgDetailScreen() {
  const { orgId = "" } = useParams();

  const { data: detail, isPending, error } = useAdminOrgDetail(orgId);
  const updateOrganization = useUpdateOrganization(orgId);

  const [draft, setDraft] = React.useState<OrgDraft | null>(null);
  const [baseline, setBaseline] = React.useState<OrgDraft | null>(null);

  // Seed ONCE per fetched record rather than on every `detail` identity change: a
  // background refetch must not throw away what the operator is halfway through
  // typing. A successful save clears the marker so the re-read reseeds both
  // halves from the server's own normalised values (it trims; we should not
  // guess how).
  const seededFor = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!detail) return;
    if (seededFor.current === detail.id) return;
    seededFor.current = detail.id;
    setDraft(draftFrom(detail));
    setBaseline(draftFrom(detail));
  }, [detail]);

  const body = draft && baseline ? changedFields(draft, baseline) : {};
  const isDirty = Object.keys(body).length > 0;

  // The two controls this form cannot submit empty, where the operator HAS
  // emptied one — `name`, whose column is NOT NULL, and `contract_start_date`,
  // whose column is nullable and whose refusal comes from the request contract
  // and the contract guard instead (`CLEARED_FIELD_MESSAGES` states both).
  //
  // Derived rather than raised on submit, because the submit is exactly what is
  // unavailable: clearing the name ALONE leaves nothing in `body`, so a
  // submit-time check would sit behind a Save button that is already disabled
  // and the operator would be left with a dead control and no reason for it.
  const clearedFields =
    draft && baseline ? clearedRequiredFields(draft, baseline) : [];
  const hasClearedField = clearedFields.length > 0;

  const readError = error ? toAdminOrgError(error) : null;
  // THE WRITE NAMES ITS SURFACE. ORG-001 is raised by three endpoints with one
  // argument-less exception, so the wire cannot say which date to move; this
  // form submits `contract_start_date` and cannot submit the end date at all
  // (the field is read-only text on the Contract card), so its refusal has to
  // land on the Start date control. Dropping this argument routes ORG-001 to a
  // key nothing on this screen reads, and the save fails silently.
  const writeError = updateOrganization.error
    ? toAdminOrgError(updateOrganization.error, "update-details")
    : null;

  // The fields whose SERVER message the cleared-field overlay below is currently
  // covering. Empty in the ordinary case: it is non-empty only after a refused
  // save whose message landed on a control the operator has since emptied.
  const overlaidWriteFields: string[] = clearedFields.filter(
    (field) => writeError?.fieldErrors[field] !== undefined,
  );

  // Not memoised, deliberately: the reset below has to read THIS render's
  // overlay, and every consumer is an inline arrow in the JSX that is rebuilt
  // each render anyway, so a stable identity would buy nothing and cost a stale
  // closure.
  const setField = (key: keyof OrgDraft, value: string) => {
    // THE STALE REFUSAL GOES WITH THE OVERLAY IT WAS HIDDEN UNDER. A refused
    // save leaves its message on the field; clearing that field covers the
    // message with `CLEARED_FIELD_MESSAGES`; and moving the field afterwards
    // used to lift the cover and put the OLD refusal back — before Save, about
    // a value the server has never seen. ORG-001 on this surface raises no
    // banner, so that resurrected sentence is the page's only notice and reads
    // exactly like a fresh one.
    //
    // The TRIGGER is the overlaid field; what it drops is the WHOLE refusal,
    // because a mutation error is one object and there is no per-field reset.
    // Stated plainly rather than glossed, and it is the right reading anyway:
    // once the operator edits the control the server complained about, every
    // message from that attempt describes a body that no longer exists. Editing
    // any other field triggers none of this and leaves the refusal standing,
    // which is the shipped behaviour everywhere else on this form.
    if (overlaidWriteFields.includes(key)) updateOrganization.reset();
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  const onSave = React.useCallback(() => {
    // Second line of defence, and honestly labelled: the disabled Save button is
    // what actually stops this today, and a native `<button disabled>` suppresses
    // `onClick`, so no test can reach this line while that holds. It is here for
    // the day the control becomes `aria-disabled` — a live possibility in this
    // codebase, which already diverges from stock shadcn on disabled-button
    // hit-testing (gh-555). What it prevents: an edit pairing a cleared name with
    // a changed billing address would otherwise save the address, succeed, and
    // let the refetch silently restore the name the operator had just erased.
    if (hasClearedField) return;
    if (!isDirty || updateOrganization.isPending) return;
    updateOrganization.mutate(body, {
      onSuccess: () => {
        // The mutation invalidates the detail key; dropping the seed marker is
        // what lets the refetched record become the new baseline, so Save goes
        // back to disabled without this screen inventing what the server stored.
        seededFor.current = null;
      },
    });
  }, [body, hasClearedField, isDirty, updateOrganization]);

  if (readError?.banner) {
    return (
      <section className="mx-auto flex w-full max-w-[var(--width-xl)] flex-col gap-6">
        <h1 id={HEADING_ID} className="display-xs text-[color:var(--text-primary)]">
          Organization
        </h1>
        <RefusalBanner banner={readError.banner} />
      </section>
    );
  }

  // Loading renders NOTHING — the sibling admin detail screen's posture.
  if (isPending || !detail || !draft) return null;

  const membersHref = `/admin/users?org_id=${encodeURIComponent(detail.id)}`;
  const orgAdminsHref = `${membersHref}&role=org_admin`;
  // The server's field errors, with the client's cleared-field refusals laid
  // over them. The overlay wins where both name a field — not because the
  // server's message has gone stale (it may not have: a 400 ORG-001 describes
  // the STORED end date, which an empty control has not moved), but because
  // while the control is empty the client's sentence is the only one the
  // operator can act on.
  //
  // What happens to the message underneath is decided in `setField`, NOT here,
  // and it is the one thing this comment used to get wrong: the covered message
  // is DROPPED when the field next moves, rather than re-emerging. The
  // re-emerging version was true as code and false as operator-facing fact —
  // `toAdminOrgError` returns `banner: null` for ORG-001 on `update-details`, so
  // that inline sentence is the page's ONLY notice of the failed save, and
  // bringing it back on a value the server has not seen is a refusal the
  // operator cannot distinguish from a fresh one.
  //
  // Editing a field the overlay never covered triggers none of it: that refusal
  // stands until the next save, as everywhere else on this form.
  const fieldErrors: AdminOrgFieldErrors = {
    ...(writeError?.fieldErrors ?? {}),
  };
  for (const field of clearedFields) {
    fieldErrors[field] = CLEARED_FIELD_MESSAGES[field];
  }

  return (
    <section className="mx-auto flex w-full max-w-[var(--width-xl)] flex-col gap-6">
      {/* CONTENT HEADER — identity, the editable name, and both CTAs. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-[280px] flex-1 flex-col gap-2">
          {/* The heading names the SAVED organization; the control beneath it
              holds what will be saved. Keeping them apart is what stops a
              half-typed rename becoming the page's own identity. */}
          <h1 id={HEADING_ID} className="display-xs text-[color:var(--text-primary)]">
            {detail.name}
          </h1>
          <FormField
            label="Organization name"
            htmlFor="org-name"
            error={fieldErrors.name}
          >
            <Input
              id="org-name"
              autoComplete="off"
              value={draft.name}
              onChange={(event) => setField("name", event.target.value)}
            />
          </FormField>
          {/* Sub-line: the CRM key and the org type. `org_type` is NEVER editable
              — the platform creates professional organizations here and personal
              ones alongside their owner, so there is nothing to choose. */}
          <p className="text-sm text-[color:var(--text-tertiary)]">
            {"External reference "}
            <strong className="text-[color:var(--text-secondary)]">
              {detail.external_reference_id ?? "Not set"}
            </strong>
            {" · Org type "}
            <strong className="capitalize text-[color:var(--text-secondary)]">
              {detail.org_type}
            </strong>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button asChild variant="secondary">
            <Link to="/admin/orgs">Cancel</Link>
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!isDirty || hasClearedField || updateOrganization.isPending}
            onClick={onSave}
          >
            {updateOrganization.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      {writeError?.banner ? (
        <Banner variant={writeError.banner.variant} title={writeError.banner.title}>
          <p>{writeError.banner.body}</p>
          {writeError.banner.code ? (
            <p className="text-sm">{`Reference code: ${writeError.banner.code}`}</p>
          ) : null}
        </Banner>
      ) : null}

      {/* THE SEAT HEADER — FOUR stats since Story 21.4 (AC16), then the FR83
          sentence, then the member deep-links that stand in for the dropped
          members table. `remaining` comes off the wire
          (`seat_capacity − seat_consumed`, computed server-side, never stored)
          so no client re-derives it and disagrees about the sign.

          `Active seats` is the fourth, and it is a TILE rather than a sub-line
          inside `Assigned` (boundary row 11): the FR83 sentence below already
          explains why `Assigned` does not fall, and burying the live figure
          inside the tile that sentence is about reads as a correction to it
          rather than a second measurement.

          The three shipped labels are UNCHANGED. Their drift against Screens
          25/26's `Capacity` / `Consumed` / `Remaining` is pre-existing and
          client-validated, and SCP §4D holds it out of scope — renaming them
          here would be an unreviewed edit to a validated screen. */}
      <div className="flex flex-col gap-3">
        <div className="grid gap-4 sm:grid-cols-4">
          <SeatStat label="Seat capacity" value={detail.seat_capacity} />
          <SeatStat label="Assigned" value={detail.seat_consumed} />
          <SeatStat label="Active seats" value={detail.active_seat_count} />
          <SeatStat label="Available" value={detail.remaining} />
        </div>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          {SEAT_STAYS_CONSUMED_SENTENCE}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button asChild variant="secondary" size="sm">
            <Link to={membersHref}>View members</Link>
          </Button>
          <Button asChild variant="secondary" size="sm">
            <Link to={orgAdminsHref}>
              {`View organization admins (${detail.org_admin_count})`}
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/*
          THE CONTRACT CARD CALL SITE — and the ONLY place the companion
          stories touch this file. Each adds one import and one prop:
            • Story 15-2 → `seatControl={…}`         (slot:seat-capacity-control)
            • Story 15-3 → `contractDateControl={…}` (slot:contract-end-date-control)
            • Story 21-1 → `renewControl={…}`        (slot:renew-contract-control)
          All three are now FILLED, one import and one prop each, exactly as
          reserved. No companion edits `ContractCard`'s internals.

          ONLY THE THIRD ONE IS CONDITIONAL, and the condition lives with the
          control rather than here: `canRenewContract` answers whether renewing
          is a thing this organization can be asked to do (AC27). The card
          refuses a persistent empty `<div data-slot=…>`, so the gate has to be
          at the call site — a control that returned `null` from inside would
          leave the slot wrapper mounted and empty.
        */}
        <ContractCard
          organization={detail}
          seatControl={
            <AdjustSeatCapacityControl
              organizationId={detail.id}
              seatCapacity={detail.seat_capacity}
              seatConsumed={detail.seat_consumed}
            />
          }
          contractDateControl={
            <ExtendContractControl
              organizationId={detail.id}
              contractEndDate={detail.contract_end_date}
            />
          }
          renewControl={
            canRenewContract(detail) ? (
              <RenewContractControl
                organizationId={detail.id}
                contractEndDate={detail.contract_end_date}
                currentTermStartedAt={detail.current_term_started_at}
                seatCapacity={detail.seat_capacity}
                activeSeatCount={detail.active_seat_count}
              />
            ) : null
          }
          externalReferenceId={draft.externalReferenceId}
          onExternalReferenceIdChange={(value) => setField("externalReferenceId", value)}
          externalReferenceIdError={fieldErrors.externalReferenceId}
          contractStartDate={draft.contractStartDate}
          onContractStartDateChange={(value) => setField("contractStartDate", value)}
          contractStartDateError={fieldErrors.contractStartDate}
        />

        <Card>
          <CardHeader>
            <CardTitle>Operational metadata</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <FormField
              label="Billing contact email"
              htmlFor="org-billing-contact-email"
              error={fieldErrors.billingContactEmail}
            >
              <Input
                id="org-billing-contact-email"
                type="email"
                autoComplete="off"
                value={draft.billingContactEmail}
                onChange={(event) => setField("billingContactEmail", event.target.value)}
              />
            </FormField>
            <FormField
              label="Security contact email"
              htmlFor="org-security-contact-email"
              error={fieldErrors.securityContactEmail}
            >
              <Input
                id="org-security-contact-email"
                type="email"
                autoComplete="off"
                value={draft.securityContactEmail}
                onChange={(event) => setField("securityContactEmail", event.target.value)}
              />
            </FormField>
            <FormField
              label="Billing address"
              htmlFor="org-billing-address"
              error={fieldErrors.billingAddress}
            >
              <Textarea
                id="org-billing-address"
                autoComplete="off"
                value={draft.billingAddress}
                onChange={(event) => setField("billingAddress", event.target.value)}
              />
            </FormField>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

export default OrgDetailScreen;
